import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { URLS } from '../../../shared/constants/api-request.urls';

@Injectable({
  providedIn: 'root',
})
export class StocksService {
  constructor(private http: HttpClient) {}

  getAllStocks(pageNumber: number, pageSize: number, searchTerm?: string, sectorId?: string): Observable<any> {
    let params = new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize);
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    if (sectorId) {
      params = params.set('sectorId', sectorId);
    }
    return this.http.get(`${URLS.STOCK}/dashboard`, { params });
  }

  getStockById(id: string): Observable<any> {
    const params = new HttpParams().set('mode', 'edit');
    return this.http.get(`${URLS.STOCK}/${id}`, { params });
  }

  createStock(stock: FormData): Observable<any> {
    return this.http.post(URLS.STOCK, stock);
  }

  updateStock(id: string, stock: FormData): Observable<any> {
    return this.http.put(`${URLS.STOCK}/${id}`, stock);
  }

  deleteStock(id: string): Observable<any> {
    return this.http.delete(`${URLS.STOCK}/${id}`);
  }

  activateStock(id: string): Observable<any> {
    return this.http.put(`${URLS.STOCK}/${id}/Activate`, {});
  }

  deactivateStock(id: string): Observable<any> {
    return this.http.put(`${URLS.STOCK}/${id}/Deactivate`, {});
  }

  getCompanyPropertyValues(companyId: string): Observable<any[]> {
    return this.http.get<any[]>(`${URLS.STOCK}/${companyId}/property-values`);
  }

  getTemplates(): Observable<any> {
    return this.http.get(`${URLS.TEMPLATE}?mode=lookup`);
  }

  getTemplateProperties(templateId: string, category: string): Observable<any> {
    let params = new HttpParams().set('TemplateId', templateId);
    const filterValue = `category="${category}"`;
    params = params.append('filter', filterValue);

    return this.http.get(URLS.PROPERTY, { params });
  }

  // Keep the existing method for backward compatibility
  getTemplatePropertiesFromSettings(templateId: string, category: string): Observable<any> {
    return this.http
      .get(URLS.PROPERTY, {
        params: new HttpParams()
          .set('TemplateId', templateId)
          .set('filter', `category="${category}"`)
          .set('mode', 'lookup'),
      })
      .pipe(
        map((response: any) => {
          return response.data || [];
        })
      );
  }

  getMarkets(): Observable<any> {
    return this.http.get(URLS.MARKET);
  }

  getIndustries(): Observable<any> {
    return this.http.get(URLS.INDUSTRY);
  }

  getSectors(): Observable<any> {
    return this.http.get(`${URLS.SECTOR}?mode=lookup`);
  }

  searchStock(searchTerm: string): Observable<any> {
    return this.http.get(`${URLS.HOME}/search-stocks?query=${searchTerm}`).pipe(
      map((response: any) => {
        return response.data.results;
      })
    );
  }

  getStockPrice(stockId: string, pageNumber: number, pageSize: number) {
    let params = new HttpParams()
      .set('stockId', stockId)
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString());
    return this.http
      .get<any>(`${URLS.STOCK_PRICE}`, {
        params,
      })
      .pipe(
        map((res) => {
          return res.data;
        })
      );
  }

  getStockPriceById(id: string): Observable<any> {
    return this.http.get(`${URLS.STOCK_PRICE}/${id}`);
  }

  createStockPrice(stockPrice: any): Observable<any> {
    return this.http.post(URLS.STOCK_PRICE, stockPrice);
  }

  updateStockPrice(id: string, stockPrice: any): Observable<any> {
    return this.http.put(`${URLS.STOCK_PRICE}/${id}`, stockPrice);
  }

  deleteStockPrice(id: string): Observable<any> {
    return this.http.delete(`${URLS.STOCK_PRICE}/${id}`);
  }

  recalculateHistoricalPrices(stockSymbol: string): Observable<any> {
    return this.http.post(`${URLS.STOCK_PRICE}/${stockSymbol}/recalculate-historical`, null).pipe(
      map((res: any) => {
        return res.data;
      })
    );
  }
}
